import { transposeChord } from "./NoteBar/Chord";
import { GUITAR, parseChord, scoreVoicing, searchVoicings, tableName } from "./NoteBar/voicings";

/**
 * '쉬운 코드' (카포): 카포를 N번 프렛에 끼우면 모든 코드를 반음 N개만큼 낮춘 모양으로 잡는다.
 * 카포 0~7 중 바레 코드가 가장 적고 잡기 쉬운 위치를 추천한다.
 */
export interface CapoChord {
  original: string; // 표시용 HTML (지금 키 기준)
  shape: string; // 카포 끼우고 잡는 모양 (표시용 HTML)
  barre: boolean;
  originalBarre: boolean;
}

export interface CapoOption {
  capo: number;
  chords: CapoChord[];
  barreCount: number;
  difficulty: number;
}

const plainName = (html: string) => html.replace(/<sup>#<\/sup>/g, "#");

// 가장 잡기 쉬운 운지법 하나로 판정: 개방현이 없고 가장 낮은 프렛을 두 줄 이상 누르면 바레
const judge = (html: string): { barre: boolean; score: number } => {
  const name = tableName(plainName(html));
  const p = parseChord(name);
  if (!p) return { barre: false, score: 3 };
  const best = searchVoicings(name, GUITAR, 1)[0];
  if (!best) return { barre: true, score: 9 };
  const score = scoreVoicing(p, best, GUITAR, name) ?? 9;
  const played = best.filter((f): f is number => f !== null);
  const hasOpen = played.some((f) => f === 0);
  const min = Math.min(...played.filter((f) => f > 0));
  const barre = !hasOpen && played.filter((f) => f === min).length >= 2;
  return { barre, score };
};

const cache = new Map<string, { barre: boolean; score: number }>();
const judgeCached = (html: string) => {
  if (!cache.has(html)) cache.set(html, judge(html));
  return cache.get(html)!;
};

export const capoOptions = (rawChords: string[], baseKey: number, maxCapo = 7): CapoOption[] => {
  const unique = Array.from(new Set(rawChords.filter((c) => c && c.trim())));
  const options: CapoOption[] = [];
  for (let capo = 0; capo <= maxCapo; capo++) {
    const shift = (((baseKey - capo) % 12) + 12) % 12;
    const seen = new Set<string>();
    const chords: CapoChord[] = [];
    let difficulty = 0;
    unique.forEach((raw) => {
      const original = transposeChord(raw, ((baseKey % 12) + 12) % 12);
      const shape = transposeChord(raw, shift);
      if (!shape || seen.has(original)) return;
      seen.add(original);
      const j = judgeCached(shape);
      difficulty += j.score + (j.barre ? 3 : 0);
      chords.push({ original, shape, barre: j.barre, originalBarre: judgeCached(original).barre });
    });
    options.push({ capo, chords, barreCount: chords.filter((c) => c.barre).length, difficulty: difficulty + capo * 0.3 });
  }
  return options;
};

export const recommendCapo = (options: CapoOption[]) =>
  options.reduce((best, o) =>
    o.barreCount < best.barreCount || (o.barreCount === best.barreCount && o.difficulty < best.difficulty) ? o : best
  , options[0]);
